import React, { useMemo } from 'react'
import NextLink from 'next/link'
import clsx from 'clsx'

import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import { PraktikKinerjaStepCard } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'

import type { StepCardStatus } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'
import type { StepProgressData } from 'app/KinerjaShared/components/PraktikKinerjaPegawaiStepCards/types'

type PersiapanStepCardProps<TStep extends string = string> = {
  className?: string
  description: string
  imagePath: string
  nextPath: string
  progressData?: StepProgressData<TStep>
  sortedSteps: Array<TStep>
  step: TStep
  title: React.ReactNode
  onClickEdit?: (step: TStep) => void
}

export function PersiapanStepCard<TStep extends string = string>({
  className,
  description,
  imagePath,
  nextPath,
  progressData = {},
  sortedSteps,
  step,
  title,
  onClickEdit,
}: PersiapanStepCardProps<TStep>) {
  const handleEditClick = () => {
    if (typeof onClickEdit !== 'function') return

    onClickEdit(step)
  }

  const stepCardStatus: StepCardStatus = useMemo(() => {
    const currentStepIndex = sortedSteps.indexOf(progressData.step)
    const persiapanStepIndex = sortedSteps.indexOf(step)

    if (currentStepIndex > persiapanStepIndex) return 'completed'
    if (currentStepIndex === persiapanStepIndex) return 'active'

    return 'locked'
  }, [progressData.step, sortedSteps, step])

  const ctaMode = stepCardStatus === 'completed' ? 'edit' : 'main'
  const isEditDisabled =
    !!progressData.appealStep && progressData.appealStep !== step
  const isEditRequested = progressData.appealStep === step

  return (
    <PraktikKinerjaStepCard
      title={title}
      description={description}
      iconSrc={imagePath}
      className={className}
      status={stepCardStatus}
      actions={
        ctaMode === 'main' ? (
          <NextLink href={nextPath} passHref>
            <Button
              size="lg"
              as="a"
              className="!inline-flex"
              type={undefined}
              color="blue"
            >
              {stepCardStatus === 'active'
                ? progressData.latestFormState === 'DRAFT'
                  ? 'Lanjutkan'
                  : 'Lakukan'
                : 'Cek rincian'}
            </Button>
          </NextLink>
        ) : isEditRequested ? (
          <div className="flex gap-1 items-center py-3 animate-pulse">
            <Icon color="informational" className="motion-safe:animate-spin">
              hourglass_empty
            </Icon>
            <Text className="!text-blue-60" color="informational">
              Perubahan sedang diajukan.
            </Text>
          </div>
        ) : (
          <Button
            variant="ghost"
            size="lg"
            color="blue"
            disabled={isEditDisabled}
            className={clsx(
              'gap-1 !bg-white text-nowrap',
              isEditDisabled && '!text-blue-40'
            )}
            onClick={handleEditClick}
          >
            <Icon color="inherit">edit_outline</Icon>
            Ubah Target Perilaku
          </Button>
        )
      }
    />
  )
}
